import { throttle } from 'lodash-es';
import React, { Fragment, useEffect, useRef, useState } from 'react';

import Buttons from '@klv/molecules/buttons';
import { generateClassName } from '@klv/shared/utils/functions';
import MixedMedia from '@klv/molecules/mixed-media';
import { Wrapper } from '@klv/utils/wrapper';

import { Pills } from './pills';
import * as styles from './styles/scrollAccordion.module.scss';

import type { IScrollAccordion } from './types';

export const ScrollAccordion = ({
  bottomHeadline,
  bottomButtons,
  customPadding = { customPaddingBottom: 'lg', customPaddingTop: 'xl' },
  fadeIn = false,
  className = '',
  rows,
  pills,
  ...props
}: IScrollAccordion) => {
  const pillsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<(HTMLDivElement | null)>(null);
    
  const [menuIsVisible, setMenuIsVisible] = useState(false);
    
  const handleScroll = throttle(() => {
    if (pillsRefs.current.length > 0) {
      const firstElement = pillsRefs.current[0];
      const secondElement = pillsRefs.current[1];
      const lastElement = pillsRefs.current[pillsRefs.current.length - 1];
  
      if (firstElement && lastElement && secondElement) {
  
        const firstElementRect = firstElement.getBoundingClientRect();
        const secondElementRect = secondElement.getBoundingClientRect();
        const lastElementRect = lastElement.getBoundingClientRect();
  
        if (lastElementRect.bottom - window.innerHeight <= 0 && !menuIsVisible) {
          setMenuIsVisible(true);
  
        } else if (firstElementRect.bottom >= secondElementRect.top) {
          setMenuIsVisible(false);
        }
      }
    }
  }, 5);
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  });
    
  return (
    <Wrapper
      backgroundColor='poppy'
      customPadding={customPadding}
      fadeIn={fadeIn ? 0.25 : 0} 
      className={generateClassName([className, styles.scrollAccordion])} {...props}
    >
      <div ref={containerRef} className={generateClassName([styles.accordionContainer, menuIsVisible ? styles.open : ''])}>
          {rows.map((row, i) => (
            <Fragment key={`${i}-${row.headline}`}>
              <div ref={(el) => (pillsRefs.current[i] = el)} style={{ backgroundColor: 'var(--color-core-poppy)' } as React.CSSProperties} className={generateClassName([ styles.rowHeadline, styles.customGridColumn])}>
                  <h2 className={generateClassName(['display1', 'inlinePadding'])}>{row.headline}</h2>
                  {i === 0 && <Pills removePills={menuIsVisible} pills={pills} />} 
              </div>
              <Wrapper className={generateClassName([styles.customGridColumn])} grid contained={false} inlinePadding={true} customPadding={{ customPaddingBottom: 'xs', customPaddingTop: 'xs' }} backgroundColor='poppy'>
                <div className={styles.rowText}>
                  {row.subhead && <h3>{row.subhead}</h3>}
                  {row.buttons && row.buttons.length > 0 && (
                    <div className={styles.buttonContainer}>
                      <Buttons
                        buttons={row.buttons}
                        buttonColor="dark"
                        setTabIndex={1}
                        variants={['primary', 'tertiary']}
                      />
                    </div>
                  )}
                  {row.links && row.links.length > 0 && (
                    <div className={styles.rowLinks}>
                      <div className={styles.linksContainer}>
                        {row.links.map((link, index) => (
                          <a href={link.url} tabIndex={1} key={`${index}-${link.url}`} target={link.openInNewTab ? '_blank' : '_self'}>{link.label}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className={generateClassName([(i === 3) ? styles.last : '', styles.rowMedia])}>
                  {row.mixedMedia && <MixedMedia mixedMedia={row.mixedMedia} />}
                </div>
              </Wrapper>
            </Fragment>
          ))}
        </div>
        {/* Bottom Section */}
        <Wrapper className={styles.customGridColumn} grid contained={false} inlinePadding={true} customPadding={{ customPaddingBottom: 'xs', customPaddingTop: 'xl' }} backgroundColor='poppy'>
          <div className={styles.subheadButton}>
            {bottomHeadline && <h3 className={generateClassName(['', styles.mainSubhead])}>{bottomHeadline}</h3>}
            {bottomButtons && bottomButtons.length > 0 && (
              <Buttons 
                buttons={bottomButtons}
                buttonColor="dark"
                setTabIndex={1}
                variants={['primary', 'tertiary']}
              />
            )}
            </div>
        </Wrapper>
    </Wrapper>
  );
};
